<template>
  <div class="page page--company-analytics">
    <div class="page__title">
      <div class="page__title-icon">
        <svg
          width="27"
          height="27"
          viewBox="0 0 27 27"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="27" height="27" rx="8" fill="#64748B" />
          <path
            d="M13.417 8.125C10.4251 8.12476 7.99976 10.5501 8 13.542C7.99976 16.534 10.4251 18.9593 13.417 18.9591C16.409 18.9593 18.8343 16.534 18.8341 13.542L13.417 13.5418V8.125Z"
            fill="white"
          />
          <path
            d="M14.5828 7V12.4173H19.9998C20.0001 9.42508 17.575 6.99952 14.5828 7Z"
            fill="white"
          />
        </svg>
      </div>
      <div class="page__title-text">{{ $t('analytics.page.title') }}</div>
    </div>
    <transactions-page-controls
      :is-filter-active="filterActive"
      :filter-object="filterObject"
      :is-transactions-loading="analyticsLoading"
      :stats="filteredAnalyticsStats"
      :stats-abac="{ data: 'analitics-statistics', action: 'read' }"
      :export-abac="{ data: 'analitics-history_export', action: 'read' }"
      :additional-filters="additionalFilters"
      :available-filter-types="$options.AVAILABLE_FILTER_TYPES"
      :auto-init-filters="true"
      @filterChanged="handleFilterChanged"
      @export="exportAnalytics"
      @refresh="loadAnalytics"
    >
      <template #extraFilter>
        <transaction-types-select
          :selected-type="selectedTransactionType"
          @typeChanged="handleTransactionTypeChanged"
        ></transaction-types-select>
      </template>
    </transactions-page-controls>
    <company-analytics-table-v2
      :is-transactions-loading="analyticsLoading"
      class="company-analytics__table"
      :transaction-type="selectedTransactionType"
      :transactions-list="analyticsTransactions"
      @parentOperationIdClicked="handleParentOperationIdClicked"
    ></company-analytics-table-v2>
  </div>
</template>

<script>
import { ADD_EXPORT } from '@vue-app/store/const/actions-types'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import AnalyticsService from '@js-app/services/analytics.service'
import moment from 'moment'
import responsiveMixin from '@vue-app/mixins/responsive-mixin'
import { mapGetters, mapState } from 'vuex'
import CompanyAnalyticsTableV2 from '@vue-app/components/company/analytics/table/CompanyAnalyticsTableV2.vue'
import { getCookie, setCookie } from '@js-app/services/helpers/cookie'
import RequireAccess from '@vue-app/components/base/role-based/RequireAccess.vue'
import TransactionsPageControls from '@vue-app/components/company/transactions/shared/transactions-page-controls/TransactionsPageControls.vue'
import {
  ADDITIONAL_FILTERS,
  AVAILABLE_FILTER_TYPES,
} from '@js-app/const/transactions-filters'
import TransactionTypesSelect from '@vue-app/components/company/analytics/filter/TransactionTypesSelect.vue'
import { Stats } from '@js-app/entities/transaction/Stats'

export default {
  name: 'CompanyAnalytics',
  components: {
    TransactionTypesSelect,
    TransactionsPageControls,
    RequireAccess,
    CompanyAnalyticsTableV2,
  },
  props: {
    id: {
      required: true,
    },
  },
  mixins: [responsiveMixin],
  DEFAULT_FILTER_OBJECT: {
    dates: {
      from: moment().subtract(1, 'days').toDate(),
      to: moment().toDate(),
    },
    currentFilterType: null,
    activeFilters: {},
  },

  data() {
    return {
      showStatisticBlock: false,
      analyticsStats: new Stats(),
      analyticsTransactions: [],
      selectedTransactionType: null,
      filterActive: false,
      filterObject: null,
      currentExportUUID: '',
      analyticsLoading: false,
      additionalFilters: ADDITIONAL_FILTERS,
    }
  },
  created() {
    this.startupPage()
  },

  AVAILABLE_FILTER_TYPES,

  watch: {
    filterObject: {
      deep: true,
      handler: 'loadAnalytics',
    },
    selectedCompany(newP, old) {
      this.startupPage()
    },
  },
  computed: {
    ...mapState(['selectedCompany']),
    ...mapGetters(['exportExisted']),

    activeFilters() {
      const _filters = {}
      const activeFilters = this.filterObject?.activeFilters || {}

      for (const _filterKey in activeFilters) {
        const filter = activeFilters[_filterKey]

        if (!filter) continue

        let value =
            filter.filterValue !== undefined && filter.filterValue !== null
                ? filter.filterValue
                : filter

        if (_filterKey === 'cardPan') {
          if (/\*/.test(value)) {
            value = value.replace(/\*+/g, '%')
          }
        }

        _filters[_filterKey] = value

        if (_filterKey === 'status' && _filters[_filterKey]?.statuses) {
          _filters[_filterKey] = _filters[_filterKey].statuses
        }
      }

      return _filters
    },

    filteredAnalyticsStats() {
      if (this.selectedTransactionType?.id) {
        let matchedStats = []

        if (typeof this.selectedTransactionType.id === 'string') {
          matchedStats = this.analyticsTransactions.filter(
            (item) => item.TransactionType === this.selectedTransactionType.id
          )
        } else if (typeof this.selectedTransactionType.id === 'number') {
          matchedStats = this.analyticsTransactions.filter(
            (item) => item.OperationTypeID === this.selectedTransactionType.id
          )
        }

        if (matchedStats.length > 0) {
          const aggregatedStats = matchedStats.reduce(
            (acc, item) => {
              const operationSum = item.OperationSum || 0
              const transactionSum = item.TransactionSum || 0

              acc.Count += 1
              acc.Sum += operationSum + transactionSum

              return acc
            },
            { Count: 0, Sum: 0 }
          )

          return new Stats({
            ...this.analyticsStats.stats,
            OperationsStats: {
              ...matchedStats[0].OperationsStats,
              All: {
                StatByCurrency: {
                  UAH: {
                    Count: aggregatedStats.Count,
                    Sum: aggregatedStats.Sum,
                  },
                },
              },
            },
          })
        } else {
          return new Stats({
            ...this.analyticsStats.stats,
            OperationsStats: {
              All: { StatByCurrency: { UAH: { Count: 0, Sum: 0 } } },
              Error: { StatByCurrency: [] },
              InProcess: { StatByCurrency: { UAH: { Count: 0, Sum: 0 } } },
              Finish: { StatByCurrency: [] },
              Returned: { StatByCurrency: null },
            },
          })
        }
      } else {
        return this.analyticsStats
      }
    },

    requestObject() {
      return {
        date_from: moment(this.filterObject.dates.from).format(
          'YYYY-MM-DD 00:00:00'
        ),
        date_to: moment(this.filterObject.dates.to).format(
          'YYYY-MM-DD 23:59:59'
        ),
        filters: this.activeFilters,
      }
    },
  },
  methods: {
    async startupPage() {
      const mids = await AnalyticsService.getMids(this.selectedCompany)

      if (mids.length === 0) {
        this.additionalFilters.mid.component = 'BaseTextInput'
      } else {
        this.additionalFilters.mid.component = 'CustomSelect'
        this.additionalFilters.mid.valueKey = 'tmp'
        this.additionalFilters.mid.options = mids.map((mid) => ({
          id: mid.ID,
          name: mid.Name,
          tmp: mid.Name,
        }))
      }

      try {
        let _analyticsFilter = JSON.parse(
          getCookie(`analytics.filter.${this.selectedCompany}`)
        )

        if (_analyticsFilter) {
          this.filterObject = _analyticsFilter
          this.filterActive = true
        } else {
          this.filterObject = this.$options.DEFAULT_FILTER_OBJECT
        }
      } catch (e) {
        console.info('analytics_filters_not_found')
      } finally {
      }
    },
    async loadAnalytics() {
      if (this.analyticsLoading) {
        return false
      }

      this.analyticsLoading = true
      try {
        let _analyticsResponse = await AnalyticsService.getCurrencies(
          this.selectedCompany,
          this.requestObject
        )
        this.analyticsStats = new Stats(_analyticsResponse.Stats)
        this.analyticsTransactions = _analyticsResponse.ReportOperations || []
      } catch (e) {
        this.analyticsTransactions = []
        console.error('failed load analytics data', e)
      } finally {
        this.analyticsLoading = false
      }
    },
    async exportAnalytics(exportType) {
      let _filtersForExport = { ...this.activeFilters }

      if (this.selectedTransactionType) {
        _filtersForExport['transaction_type'] = this.selectedTransactionType.id
      }

      let requestObject = {
        date_from: moment(this.filterObject.dates.from).format(
          'YYYY-MM-DD 00:00:00'
        ),
        date_to: moment(this.filterObject.dates.to).format(
          'YYYY-MM-DD 23:59:59'
        ),
        filters: _filtersForExport,
        type: exportType.id,
      }

      let _filterString = JSON.stringify(requestObject)

      if (this.exportExisted('analytics', _filterString)) {
        return
      }

      let _analyticsExportResponse = await CompaniesApi.exportCompanyAnalytics(
        this.id,
        requestObject
      )

      await this.$store.dispatch(ADD_EXPORT, {
        ..._analyticsExportResponse.data,
        section: 'analytics',
        filterString: _filterString,
      })
    },
    handleTransactionTypeChanged(transactionType) {
      this.selectedTransactionType = transactionType
    },

    handleFilterChanged(newFilterObject, changeType) {
      this.filterObject = { ...newFilterObject }

      this.filterActive = changeType === 'apply'

      if (changeType === 'reset') {
        setCookie(`analytics.filter.${this.id}`, null, 0)
      } else if (changeType === 'apply') {
        setCookie(
          `analytics.filter.${this.id}`,
          JSON.stringify(this.filterObject),
          0.5
        )
      }
    },

    handleParentOperationIdClicked(operationId) {
      this.filterObject = {
        ...this.filterObject,
        currentFilterType: this.$options.AVAILABLE_FILTER_TYPES.find(
          (filterType) => filterType.id === 'operationId'
        ),
        activeFilters: {
          ...this.filterObject.activeFilters,
          operationId: operationId,
        },
      }

      this.filterActive = true
      this.selectedTransactionType = null
    },
  },
}
</script>

<style lang="scss" scoped>
.page {
  &--company-analytics {
    margin-top: 30px;
  }

  &__title {
    display: flex;
    align-items: center;
    margin-bottom: 30px;

    &-icon {
      margin-right: 13px;
      width: 27px;
      height: 27px;

      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        width: 100%;
        height: 100%;
      }
    }

    &-text {
      font-weight: 600;
      font-size: 20px;
      line-height: 24px;
      display: flex;
      align-items: center;
      color: #585f6d;
    }
  }
}
.company-analytics {
  &__table {
    margin-top: 10px;
  }
}
</style>
